import React, { useEffect, useState } from "react";
import { Layout } from "@/components/Layout";
import { PageHero } from "@/components/PageHero";
import { api } from "@/lib/api";
import { Copy, Loader2, Wand2 } from "lucide-react";

export default function TrendsLibrary() {
  const [trends, setTrends] = useState([]);
  const [idea, setIdea] = useState("");
  const [selected, setSelected] = useState(null);
  const [outputType, setOutputType] = useState("image");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/trends").then(r => { setTrends(r.data); setSelected(r.data[0]); }); }, []);

  const enhance = async () => {
    if (!idea.trim() || !selected) return;
    setLoading(true); setResult(null);
    try {
      const r = await api.post("/prompts/enhance", { idea, style: selected.name, output_type: outputType });
      setResult(r.data);
    } finally { setLoading(false); }
  };

  return (
    <Layout hideRightPanel>
      <PageHero
        testId="trends-hero"
        eyebrow="Visual Trends · Prompt Forge"
        title={<>50 years of <span className="italic">visual language.</span></>}
        sub="Every decade's dominant aesthetic, distilled with color palettes and prompt seeds. Pair with our Prompt Enhancer to generate ultra-detailed, generation-ready prompts."
      />

      {selected && (
        <div className="curio-card p-6 mb-8" data-testid="prompt-forge">
          <div className="text-xs uppercase tracking-widest font-bold text-[#F97316] mb-2">Prompt Forge</div>
          <div className="flex flex-col md:flex-row gap-3 items-stretch">
            <input value={idea} onChange={e => setIdea(e.target.value)}
              placeholder="e.g., a woman drinking coffee at a diner"
              className="flex-1 bg-[#F7F9FC] border border-[#E3E8F0] rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#0B1220]"
              data-testid="forge-idea-input" />
            <select value={outputType} onChange={e => setOutputType(e.target.value)}
              className="bg-[#F7F9FC] border border-[#E3E8F0] rounded-xl px-4 py-2.5 text-sm">
              <option value="image">Image</option><option value="video">Video</option><option value="text">Text</option>
            </select>
            <button onClick={enhance} disabled={loading || !idea.trim()} className="btn-coral disabled:opacity-50" data-testid="forge-run">
              {loading ? <><Loader2 size={14} className="animate-spin" /> Forging…</> : <><Wand2 size={14} /> Enhance in {selected.name}</>}
            </button>
          </div>
          {result && (
            <div className="mt-4 bg-[#FAFBFD] border border-[#E3E8F0] rounded-xl p-4 font-mono text-sm leading-relaxed">
              {result.enhanced}
              <button onClick={() => navigator.clipboard.writeText(result.enhanced)} className="btn-ghost text-xs mt-3" data-testid="forge-copy"><Copy size={11} /> Copy</button>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 stagger" data-testid="trends-grid">
        {trends.map(t => (
          <button key={t.name} onClick={() => setSelected(t)}
            className={`curio-card p-5 text-left ${selected?.name === t.name ? '!border-[#0B1220] !border-2' : ''}`}
            data-testid={`trend-${t.name.replace(/\s+/g, '-').toLowerCase()}`}>
            <div className="text-[10px] uppercase tracking-widest text-[#5B6478] font-bold">{t.decade} · {t.year}</div>
            <h3 className="font-display text-2xl font-semibold mt-1 mb-2">{t.name}</h3>
            <p className="text-sm text-[#0B1220]/80 leading-relaxed mb-3">{t.desc}</p>
            <div className="flex gap-1 mb-3">
              {t.palette.map(c => <div key={c} className="w-8 h-8 rounded-lg border border-[#E3E8F0]" style={{ background: c }} title={c} />)}
            </div>
            <div className="text-xs text-[#5B6478] font-mono truncate">{t.prompt_seed}</div>
          </button>
        ))}
      </div>
    </Layout>
  );
}
